import { RoutablePage } from '@gig/magic-common/dist/routing/index';
import { RouteName } from '../../../routes/routes.constants';
import { config } from '../../config';

export enum LobbiesMap {
    GAMES = 'casino',
    HOME = 'casino',
    LIVE_CASINO = 'liveCasino',
    FREE_TO_PLAY_HOMEPAGE = 'freeToPlayHomepage',
    FREE_TO_PLAY_LOBBY_GAMEPLAY = 'freeToPlay',

    // NOTE: only used to "tag" a subcategory, not a real lobby
    USER_RECOMMENDATIONS = 'userRecommendations',
    MARKET_RECOMMENDATIONS = 'marketRecommendations',
    FREE_TO_PLAY = 'freeToPlay',
}

export const lobbyToRoutablePageMap: Record<string, RoutablePage> = {
    games: RoutablePage.GAMES,
    casino: RoutablePage.GAMES,
    liveCasino: RoutablePage.LIVE_CASINO,
    freeToPlayHomepage: RoutablePage.FREE_TO_PLAY_HOMEPAGE,
    freeToPlay: RoutablePage.FREE_TO_PLAY,
};

/**
 * The available lobby paths
 */
enum LobbiesPaths {
    GAMES = 'games',
    LIVE_CASINO = 'live-casino',
    FREE_TO_PLAY_HOMEPAGE = 'free-to-play-homepage',
    FREE_TO_PLAY_LOBBY_GAMEPLAY = 'free-to-play',
}

export enum LobbyType {
    FREE_TO_PLAY_HOMEPAGE = 'freeToPlayHomepage',
    FREE_TO_PLAY_LOBBY_GAMEPLAY = 'freeToPlay',
    LOBBY = 'lobby',
}

/**
 * Maps the lobby value present on the game with the frontend route
 */
export const lobbiesPathsMap: Record<string, string> = {
    casino: LobbiesPaths.GAMES,
    liveCasino: LobbiesPaths.LIVE_CASINO,
    freeToPlayHomepage: LobbiesPaths.FREE_TO_PLAY_HOMEPAGE,
    freeToPlay: LobbiesPaths.FREE_TO_PLAY_LOBBY_GAMEPLAY,
};

const urlRouteMap: Record<string, RouteName> = {
    [LobbiesPaths.GAMES]: RouteName.GAMES,
    [LobbiesPaths.LIVE_CASINO]: RouteName.LIVE_CASINO,
    [LobbiesPaths.FREE_TO_PLAY_HOMEPAGE]: RouteName.FREE_TO_PLAY_HOMEPAGE,
    [LobbiesPaths.FREE_TO_PLAY_LOBBY_GAMEPLAY]:
        RouteName.FREE_TO_PLAY_LOBBY_GAMEPLAY,
};

const routeNameLobbyMap: Partial<Record<RouteName, string>> = {
    [RouteName.GAMES]: 'casino',
    [RouteName.HOME]: 'casino',
    [RouteName.LIVE_CASINO]: 'liveCasino',
    [RouteName.FREE_TO_PLAY_HOMEPAGE]: 'freeToPlayHomepage',
    [RouteName.FREE_TO_PLAY_LOBBY_GAMEPLAY]: 'freeToPlay',
    [RouteName.FREE_TO_PLAY_LOBBY]: 'freeToPlayLobby',
};

export const getLobbyFromRouteName = (routeName: string) => {
    return routeNameLobbyMap[routeName as RouteName];
};

export const getPathFromLobbyMap = (lobby: string) =>
    lobbiesPathsMap[lobby] || lobby;

export const getPathFromLobby = (lobby: string) => {
    const key = Object.keys(LobbiesMap).find(
        (key) => LobbiesMap[key as keyof typeof LobbiesMap] === lobby,
    );
    return RouteName[key as keyof typeof RouteName];
};

/**
 * Tries to detect a valid lobby path from the url
 * @returns string
 */
export const getLobbyFromUrl = (): string => {
    const lobbyOnPath = getLobbyFromPathname();
    const validLobbies: string[] = Object.values(LobbiesPaths);
    const isAValidLobby = validLobbies.includes(lobbyOnPath);

    return isAValidLobby ? lobbyOnPath : LobbiesPaths.GAMES;
};

/**
 * Tries to detect lobby from the url
 * else tries to find it in the lobbiesPathsMap
 * using the first lobby passed
 * @param lobbies game lobbies
 * @returns
 */
export const getLobbyPath = (lobbies: string[]): string => {
    const lobbyFromUrl = getLobbyFromUrl();

    if (lobbyFromUrl) {
        return lobbyFromUrl;
    }

    if (lobbies && lobbies.length > 0 && lobbiesPathsMap[lobbies[0]]) {
        return lobbiesPathsMap[lobbies[0]];
    }

    return LobbiesPaths.GAMES;
};

/**
 * Gets the first lobby path from the argument array or defaults to games
 * @param lobbies game lobbies
 * @returns
 */
export const getFirstLobbyPath = (lobbies: string[]): string => {
    if (lobbies && lobbies.length > 0 && lobbiesPathsMap[lobbies[0]]) {
        return lobbiesPathsMap[lobbies[0]];
    }

    return LobbiesPaths.GAMES;
};

/**
 * Detects which route to use depending on url
 * e.g. if live-casino is in url it will return RouteName.LIVE_CASINO
 * @returns RouteName
 */
export const detectRouteNameFromUrl = (): RouteName => {
    const lobby = getLobbyFromPathname();
    return urlRouteMap[lobby] ?? RouteName.GAMES;
};

function getLobbyFromPathname() {
    const singleLanguage = config.languages.available.length === 1;
    const lobbyIndex = singleLanguage ? 1 : 2;
    return window.location.pathname.split('/')[lobbyIndex];
}

export const GAMES_LENGTH_FOR_BIG_THUMBNAIL = 2;
export const GAMES_LENGTH_FOR_BIG_MOBILE_THUMBNAIL = 3;
